// The UI's structural CSS, injected once per document. Colours, font and corner radius are custom properties the game
// sets through its theme; text size is `--rpg-text-scale`; reduce motion is the `data-reduce-motion` attribute on the
// root. Targets are at least 48 px for touch; every focusable shows a ring.

/** The game's look for screens. Every value is a CSS value. */
export interface UiTheme {
  font: string;
  /** Body text. */
  text: string;
  /** Secondary text (hints, empty slots). */
  muted: string;
  /** Behind full-screen screens (the rotate prompt). */
  bg: string;
  /** A screen's panel. */
  panel: string;
  /** The dim behind a screen over play. */
  scrim: string;
  /** Behind a splash screen (the title), so its text reads over any scene. */
  splash: string;
  button: string;
  buttonHover: string;
  /** Selected and primary items. */
  accent: string;
  /** Text on the accent. */
  accentText: string;
  /** The focus ring. */
  focus: string;
  /** Warnings (a binding conflict). */
  warn: string;
  radius: string;
}

/** A neutral dark theme with AA contrast; games pass their own. */
export const DEFAULT_THEME: UiTheme = {
  font: "system-ui, sans-serif",
  text: "#f4f1ea",
  muted: "#c9c4b8",
  bg: "#1c1f22",
  panel: "rgba(28, 31, 34, 0.94)",
  scrim: "rgba(0, 0, 0, 0.45)",
  splash: "linear-gradient(rgba(0, 0, 0, 0.15), rgba(0, 0, 0, 0.6))",
  button: "#33383d",
  buttonHover: "#454c53",
  accent: "#f0c75e",
  accentText: "#1c1f22",
  focus: "#7fd1ff",
  warn: "#ffb3a0",
  radius: "10px",
};

/** The theme as custom properties on the root. */
export function themeVars(t: UiTheme): Record<string, string> {
  return {
    "--rpg-font": t.font,
    "--rpg-text": t.text,
    "--rpg-muted": t.muted,
    "--rpg-bg": t.bg,
    "--rpg-panel": t.panel,
    "--rpg-scrim": t.scrim,
    "--rpg-splash": t.splash,
    "--rpg-button": t.button,
    "--rpg-button-hover": t.buttonHover,
    "--rpg-accent": t.accent,
    "--rpg-accent-text": t.accentText,
    "--rpg-focus": t.focus,
    "--rpg-warn": t.warn,
    "--rpg-radius": t.radius,
  };
}

export const UI_CSS = `
.rpg-ui {
  --rpg-safe-top: env(safe-area-inset-top, 0px);
  --rpg-safe-right: env(safe-area-inset-right, 0px);
  --rpg-safe-bottom: env(safe-area-inset-bottom, 0px);
  --rpg-safe-left: env(safe-area-inset-left, 0px);
  --rpg-text-scale: 1;
  position: fixed; inset: 0; z-index: 900; pointer-events: none;
  font-family: var(--rpg-font); font-size: calc(16px * var(--rpg-text-scale)); line-height: 1.4; color: var(--rpg-text);
}
.rpg-ui *, .rpg-ui *::before, .rpg-ui *::after { box-sizing: border-box; }
.rpg-layer {
  position: absolute; inset: 0; pointer-events: auto; display: flex; align-items: center; justify-content: center;
  padding: max(12px, var(--rpg-safe-top)) max(12px, var(--rpg-safe-right)) max(12px, var(--rpg-safe-bottom)) max(12px, var(--rpg-safe-left));
}
.rpg-layer[data-dim] { background: var(--rpg-scrim); }
.rpg-layer[data-variant="splash"] { background: var(--rpg-splash); }
.rpg-layer[data-variant="dock"] { pointer-events: none; justify-content: flex-start; align-items: flex-start; }
.rpg-screen[data-variant="dock"] { pointer-events: auto; width: min(19em, 100%); padding: 0.75em 0.9em 0.9em; }
.rpg-photo-surface { position: absolute; inset: 0; pointer-events: auto; touch-action: none; cursor: grab; }
.rpg-photo-surface:active { cursor: grabbing; }
.rpg-screen {
  width: min(640px, 100%); max-height: 100%; overflow: auto; overscroll-behavior: contain;
  background: var(--rpg-panel); border-radius: var(--rpg-radius); padding: 1em 1.25em 1.25em;
  box-shadow: 0 8px 30px rgba(0,0,0,0.35);
}
.rpg-screen[data-variant="splash"] { background: none; box-shadow: none; text-align: center; width: auto; text-shadow: 0 1px 3px rgba(0,0,0,0.6); }
.rpg-screen-head { display: flex; align-items: center; gap: 0.5em; margin-bottom: 0.75em; }
.rpg-screen h1 { margin: 0; font-size: 1.6em; font-weight: 650; letter-spacing: 0.01em; }
.rpg-screen[data-variant="splash"] h1 { font-size: clamp(2em, 6vw, 3.2em); margin-bottom: 0.4em; }
.rpg-screen h2 { margin: 1em 0 0.4em; font-size: 1.05em; color: var(--rpg-muted); font-weight: 600; text-transform: uppercase; letter-spacing: 0.06em; }
.rpg-menu { display: flex; flex-direction: column; gap: 0.5em; align-items: stretch; min-width: min(18em, 100%); margin: 0 auto; }
.rpg-btn {
  min-height: 48px; min-width: 48px; padding: 0.55em 1.1em; font: inherit; color: var(--rpg-text);
  background: var(--rpg-button); border: 2px solid transparent; border-radius: var(--rpg-radius);
  cursor: pointer; touch-action: manipulation; text-align: center;
}
.rpg-btn:hover { background: var(--rpg-button-hover); }
.rpg-btn[data-variant="primary"], .rpg-btn[aria-checked="true"], .rpg-btn[aria-selected="true"] { background: var(--rpg-accent); color: var(--rpg-accent-text); }
.rpg-btn:disabled { opacity: 0.55; cursor: default; }
.rpg-icon-btn { padding: 0; width: 48px; height: 48px; display: inline-flex; align-items: center; justify-content: center; }
.rpg-icon-btn svg { width: 26px; height: 26px; }
.rpg-ui :focus { outline: 3px solid var(--rpg-focus); outline-offset: 2px; }
.rpg-row { display: flex; align-items: center; justify-content: space-between; gap: 0.75em; min-height: 52px; flex-wrap: wrap; }
.rpg-row > label, .rpg-row > .rpg-label { flex: 1 1 10em; }
.rpg-options { display: flex; gap: 0.35em; flex-wrap: wrap; }
.rpg-slider { display: flex; align-items: center; gap: 0.6em; }
.rpg-slider input { width: min(12em, 40vw); height: 48px; accent-color: var(--rpg-accent); }
.rpg-slider output { min-width: 3.2em; text-align: right; color: var(--rpg-muted); font-variant-numeric: tabular-nums; }
.rpg-field input {
  flex: 1 1 12em; min-height: 48px; padding: 0.4em 0.7em; font: inherit; color: var(--rpg-text);
  background: var(--rpg-bg); border: 2px solid var(--rpg-muted); border-radius: var(--rpg-radius);
}
.rpg-tabs { display: flex; gap: 0.35em; flex-wrap: wrap; margin-bottom: 0.5em; }
.rpg-hint { color: var(--rpg-muted); margin: 0.3em 0; }
.rpg-warn { color: var(--rpg-warn); margin: 0.2em 0 0; flex-basis: 100%; }
.rpg-status { min-height: 1.4em; color: var(--rpg-muted); }
.rpg-list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 0.35em; }
.rpg-list-row { display: flex; align-items: center; gap: 0.7em; min-height: 52px; padding: 0.25em 0.4em; border-radius: var(--rpg-radius); background: rgba(255,255,255,0.04); flex-wrap: wrap; }
.rpg-list-icon { width: 36px; height: 36px; flex: 0 0 36px; display: inline-flex; }
.rpg-list-icon svg { width: 100%; height: 100%; }
.rpg-list-text { flex: 1 1 10em; display: flex; flex-direction: column; min-width: 0; }
.rpg-list-title { font-weight: 600; }
.rpg-list-badge { color: var(--rpg-accent); font-size: 0.85em; }
.rpg-list-detail { color: var(--rpg-muted); font-size: 0.9em; }
.rpg-list-actions { display: flex; gap: 0.35em; flex-wrap: wrap; }
.rpg-meter { display: flex; align-items: center; gap: 0.6em; min-height: 40px; flex-wrap: wrap; }
.rpg-meter-bar { flex: 1 1 8em; height: 12px; border-radius: 6px; background: rgba(255,255,255,0.12); overflow: hidden; }
.rpg-meter-fill { height: 100%; background: var(--rpg-accent); }
.rpg-meter-text { color: var(--rpg-muted); font-variant-numeric: tabular-nums; }
.rpg-rotate {
  position: fixed; inset: 0; z-index: 1000; pointer-events: auto; display: flex; flex-direction: column; gap: 1em;
  align-items: center; justify-content: center; background: var(--rpg-bg); padding: 24px; text-align: center; font-size: 1.2em;
}
.rpg-rotate svg { width: 72px; height: 72px; }
.rpg-ui[data-contrast] .rpg-btn { border-color: var(--rpg-text); }
.rpg-ui[data-contrast] .rpg-btn[data-variant="primary"], .rpg-ui[data-contrast] .rpg-btn[aria-checked="true"], .rpg-ui[data-contrast] .rpg-btn[aria-selected="true"] { border-color: var(--rpg-accent); }
.rpg-ui[data-contrast] .rpg-screen { border: 2px solid var(--rpg-text); }
.rpg-ui[data-contrast] .rpg-screen[data-variant="splash"] { border: none; }
.rpg-ui[data-reduce-motion] *, .rpg-ui[data-reduce-motion] *::before, .rpg-ui[data-reduce-motion] *::after { transition: none !important; animation: none !important; }
`;

/** Put the CSS in the document once. */
export function injectStyle(doc: Document): void {
  if (doc.getElementById("rpg-ui-style")) return;
  const s = doc.createElement("style");
  s.id = "rpg-ui-style";
  s.textContent = UI_CSS;
  doc.head.appendChild(s);
}
